import * as t from 'io-ts';

import {RViewerPanelId} from '../../store/panels/panelStateBase';
import {RActivityIdRef} from './activityModel';
import {
  parseAnnotation,
  RAnnotation,
  RHighlightAnnotation,
  RHighlightCommentAnnotation,
  RSurveyAnnotation,
} from './annotationModel';
import {
  makeApiType,
  makeDefaultValueType,
  makeEnumRuntimeType,
  RCommentUidRef,
  RConversationIdRef,
  RLazyTeammateIdRef,
} from './apiTypes';
import {
  parseSimpleAttachment,
  parseUploadedAttachment,
  RAttachment,
  RSimpleAttachment,
} from './attachmentModel';
import {parseCommentReaction, RCommentReaction} from './commentReactionModel';
import {parseCommentUnfurl, RCommentUnfurl} from './commentUnfurlModel';

/*
 * Runtime type.
 */

export enum CommentStatusesEnum {
  DRAFT = 'draft',
  PENDING = 'pending',
  SENDING = 'sending',
  FAILED = 'failed',
  SENT = 'sent',
}

export enum CommentErrorsEnum {
  CANT_SAVE = 'CANT_SAVE',
}

const RCommentStatus = makeEnumRuntimeType(CommentStatusesEnum);
const RCommentError = makeEnumRuntimeType(CommentErrorsEnum);

// The last comment should not reference the conversation to avoid a circular reference.
const RLastCommentConversationRef = t.type({
  isLastComment: t.literal(true),
  conversationId: t.undefined,
});

const RNonLastCommentConversationRef = t.type({
  isLastComment: t.literal(false),
  conversationId: RConversationIdRef,
});

// TODO: Remove this when the garbage collector can handle cycles and we can just use comment.conversationId.
const RCommentConversationRef = t.union([RLastCommentConversationRef, RNonLastCommentConversationRef]);

export const RComment = t.intersection(
  [
    t.type({
      uid: t.string,
      url: t.string,
      status: RCommentStatus,
      version: t.string,
      authorId: RLazyTeammateIdRef,
      conversationId: t.number,
      conversationRef: RCommentConversationRef,
      text: t.string,
      attachments: t.readonlyArray(t.union([RSimpleAttachment, RAttachment])),
      isSaving: t.boolean,
      sentAt: t.Integer,
      createdAt: t.Integer,
      updatedAt: t.Integer,
      reactions: t.readonlyArray(RCommentReaction),
      unfurls: makeDefaultValueType(t.readonlyArray(RCommentUnfurl), []),
    }),
    t.partial({
      id: t.number,
      error: RCommentError,
      lastPanelId: RViewerPanelId,
      /** ID of the offline task that is scheduled to send the comment. */
      sendOfflineTaskId: t.string,
      lastEditedAt: t.Integer,
      teammateIds: t.readonlyArray(RLazyTeammateIdRef),
      mentions: t.readonlyArray(t.string),
      topic: t.string,
      referencedActivityId: RActivityIdRef,
      annotation: RAnnotation,
    }),
  ],
  'comment',
);
RCommentUidRef.setTargetType(RComment);

export const RHighlightMessageAnnotationComment = t.intersection([
  RComment,
  t.type({
    // the referencedActivityId and Annotation already optionally exist on a
    // normal comment, but we want to make it non-optional on an annotated comment
    referencedActivityId: RActivityIdRef,
    annotation: RHighlightAnnotation,
  }),
]);

export const RSurveyAnnotationComment = t.intersection([
  RComment,
  t.type({
    referencedActivityId: RActivityIdRef,
    annotation: RSurveyAnnotation,
  }),
]);

export const RHighlightCommentAnnotationComment = t.intersection([
  RComment,
  t.type({
    referencedActivityId: RActivityIdRef,
    annotation: RHighlightCommentAnnotation,
  }),
]);

export const RAnnotationComment = t.union(
  [RHighlightMessageAnnotationComment, RSurveyAnnotationComment, RHighlightCommentAnnotationComment],
  'annotationComment',
);

/*
 * Type.
 */

export interface CommentModel extends Readonly<t.TypeOf<typeof RComment>> {}

export interface DraftCommentModel extends CommentModel {
  status: CommentStatusesEnum.DRAFT;
}

export interface HighlightMessageAnnotationCommentModel
  extends Readonly<t.TypeOf<typeof RHighlightMessageAnnotationComment>> {}
export interface SurveyAnnotationCommentModel extends Readonly<t.TypeOf<typeof RSurveyAnnotationComment>> {}
export interface HighlightCommentAnnotationCommentModel
  extends Readonly<t.TypeOf<typeof RHighlightCommentAnnotationComment>> {}
export type AnnotationCommentModel = HighlightMessageAnnotationCommentModel | SurveyAnnotationCommentModel;

/*
 * IO methods.
 */

export function parseComment(src: any): CommentModel {
  return {
    id: src.id,
    uid: src.uid,
    url: src.url,
    status: src.status,
    version: (src.updated_at || Date.now()).toString(),
    authorId: src.author_id || src.author.id,
    conversationId: src.conversation_id,
    conversationRef: {
      isLastComment: false,
      conversationId: src.conversation_id,
    },
    text: src.text,
    attachments: src.attachments && src.attachments.map(parseUploadedAttachment),
    mentions: (Array.isArray(src.mentions) && src.mentions) || [],
    isSaving: false,
    sentAt: src.date,
    lastEditedAt: src.last_edited_at || undefined,
    createdAt: src.created_at,
    updatedAt: src.updated_at || src.created_at, // TEMPORARY.,
    reactions: Array.isArray(src.reactions)
      ? src.reactions.map((reaction: any) => parseCommentReaction(reaction))
      : [],
    referencedActivityId: src.referenced_activity_id || undefined,
    annotation: src.annotation ? parseAnnotation(src.annotation) : undefined,
    unfurls: Array.isArray(src.unfurls) ? src.unfurls.map((unfurl: any) => parseCommentUnfurl(unfurl)) : [],
  };
}

export function parseLastComment(conversationSrc: any): CommentModel {
  // Most of the last comment can be parsed with the same logic.
  const lastComment = conversationSrc.last_comment;
  const parsedComment = parseComment({
    ...lastComment,
    uid: `partial_${lastComment.uid}`,
    url: `${conversationSrc.url}/comments/${lastComment.uid}`,
    status: CommentStatusesEnum.SENT,
    conversation_id: conversationSrc.id,
    attachments: undefined,
  });

  // Except for the attachments.
  return {
    ...parsedComment,
    conversationRef: {
      isLastComment: true,
      conversationId: undefined,
    },
    attachments: lastComment.attachments && lastComment.attachments.map(parseSimpleAttachment),
  };
}

/*
 * API type.
 */

export const ApiComment = makeApiType('comment', RComment, parseComment);
